/**
 * RelatedRecordsCard — bidirectional links between Product Dev records.
 *
 * A record can be tied to others as a shared FABRIC, part of a COLLECTION, or
 * simply RELATED. Links are stored once and surfaced from both sides (the
 * backend returns whichever record isn't the one being viewed), so a link made
 * on style A also shows on style B.
 *
 * Visual language mirrors LinkedProductDevCard (advisory chip list with a
 * type badge + linked style name). The "Link a record" affordance mirrors
 * InlineProductSearch's server-side command palette, then a type Select and an
 * optional note before committing.
 *
 * Backend seam lives in ../utils/pipeline-procedures (single cast point) while
 * the parallel backend PR lands.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronsUpDown, Link2, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { CardRowsSkeleton } from '@/features/product-dev/components/skeletons';
import {
  useCreateLink,
  useDeleteLink,
  useRecordLinks,
  useSearchRecords,
  type RecordLinkType,
  type SearchRecordResult,
} from '../utils/pipeline-procedures';

const LINK_TYPES: readonly RecordLinkType[] = ['fabric', 'collection', 'related'];

/** Muted, differentiated tints — quiet enough not to read as status accents
 *  (DESIGN.md §7 pill discipline), distinct enough to scan the link kind. */
const LINK_TYPE_BADGE: Record<RecordLinkType, string> = {
  fabric: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200/70',
  collection: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/70',
  related: 'bg-muted text-muted-foreground ring-1 ring-inset ring-black/5',
};

const useDebounced = (value: string, delayMs = 250): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
};

export function RelatedRecordsCard({
  productDevId,
  canEdit,
}: {
  productDevId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation('productDev');
  const linksQuery = useRecordLinks(productDevId);
  const links = linksQuery.data ?? [];

  const [adding, setAdding] = useState(false);

  // Read-only + no links: stay quiet like LinkedProductDevCard (zero noise).
  if (!canEdit && !linksQuery.isLoading && links.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" aria-hidden="true" />
          <CardTitle className="text-base">{t('relatedRecords.title')}</CardTitle>
          {links.length > 0 && (
            <span className="text-[11px] tabular-nums text-muted-foreground">{links.length}</span>
          )}
        </div>
        {canEdit && !adding && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-3.5 w-3.5" /> {t('relatedRecords.add')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-1">
        {linksQuery.isLoading ? (
          <CardRowsSkeleton rows={2} />
        ) : linksQuery.error ? (
          <p className="text-sm text-destructive">{linksQuery.error.message}</p>
        ) : (
          <>
            {links.length === 0 && !adding && (
              <p className="pb-1 text-xs text-muted-foreground">{t('relatedRecords.empty')}</p>
            )}

            <ul className="space-y-0.5">
              {links.map((link) => (
                <li
                  key={link.id}
                  className="group flex items-center gap-2.5 rounded-md px-1.5 py-1.5 -mx-1.5 transition-colors hover:bg-muted/40"
                >
                  <Link
                    to={`/product-dev/${link.otherId}`}
                    className="shrink-0"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <LinkThumb url={link.otherHeroUrl} />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/product-dev/${link.otherId}`}
                        className="min-w-0 truncate text-sm font-medium hover:underline"
                      >
                        {link.otherStyleName}
                      </Link>
                      <span
                        className={cn(
                          'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                          LINK_TYPE_BADGE[link.linkType],
                        )}
                      >
                        {t(`relatedRecords.type.${link.linkType}`)}
                      </span>
                    </div>
                    {(link.otherStyleCode || link.note) && (
                      <p className="truncate text-xs text-muted-foreground">
                        {link.otherStyleCode && (
                          <span className="font-mono text-[10px] uppercase tracking-wider">
                            {link.otherStyleCode}
                          </span>
                        )}
                        {link.otherStyleCode && link.note && ' · '}
                        {link.note}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <RemoveLinkButton linkId={link.id} onRemoved={() => linksQuery.refetch()} />
                  )}
                </li>
              ))}
            </ul>

            {canEdit && adding && (
              <AddLinkForm
                productDevId={productDevId}
                excludeIds={[productDevId, ...links.map((l) => l.otherId)]}
                onDone={() => {
                  setAdding(false);
                  linksQuery.refetch();
                }}
                onCancel={() => setAdding(false)}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// LinkThumb — 40×40 hero thumbnail of the linked record (same size language as
// the Variants card's swatch cell). Falls back to a quiet link glyph.
// ----------------------------------------------------------------------------

function LinkThumb({ url }: { url: string | null }) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [url]);

  return (
    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/50">
      {url && !imgError ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <Link2 className="h-4 w-4 text-muted-foreground/50" aria-hidden="true" />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// RemoveLinkButton — hover-reveal X (matches TrimsCard's remove affordance).
// ----------------------------------------------------------------------------

function RemoveLinkButton({ linkId, onRemoved }: { linkId: string; onRemoved: () => void }) {
  const { t } = useTranslation('productDev');
  const deleteLink = useDeleteLink();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await deleteLink.mutateAsync({ linkId });
      onRemoved();
    } catch (err) {
      toast({
        title: t('relatedRecords.toast.removeFailed'),
        description: err instanceof Error ? err.message : t('relatedRecords.toast.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void remove()}
      disabled={busy}
      className="ml-auto shrink-0 p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
      aria-label={t('relatedRecords.aria.remove')}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
    </button>
  );
}

// ----------------------------------------------------------------------------
// AddLinkForm — record picker (server search) + type Select + optional note.
// ----------------------------------------------------------------------------

function AddLinkForm({
  productDevId,
  excludeIds,
  onDone,
  onCancel,
}: {
  productDevId: string;
  excludeIds: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('productDev');
  const createLink = useCreateLink();

  const [picked, setPicked] = useState<SearchRecordResult | null>(null);
  const [linkType, setLinkType] = useState<RecordLinkType>('related');
  const [note, setNote] = useState('');

  const submit = async () => {
    if (!picked || createLink.isPending) return;
    try {
      await createLink.mutateAsync({
        productDevId,
        otherId: picked.id,
        linkType,
        note: note.trim() || null,
      });
      onDone();
    } catch (err) {
      toast({
        title: t('relatedRecords.toast.linkFailed'),
        description: err instanceof Error ? err.message : t('relatedRecords.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2.5">
      <RecordPicker excludeIds={excludeIds} value={picked} onPick={setPicked} />
      <div className="flex items-center gap-2">
        <Select value={linkType} onValueChange={(v) => setLinkType(v as RecordLinkType)}>
          <SelectTrigger className="h-8 w-[8rem] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LINK_TYPES.map((lt) => (
              <SelectItem key={lt} value={lt}>
                {t(`relatedRecords.type.${lt}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('relatedRecords.notePlaceholder')}
          className="h-8 flex-1 text-sm"
          maxLength={300}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onCancel}>
          {t('relatedRecords.cancel')}
        </Button>
        <Button
          size="sm"
          className="h-7 gap-1 bg-threadheads-purple px-2 text-xs text-white hover:bg-threadheads-purple/90"
          disabled={!picked || createLink.isPending}
          onClick={() => void submit()}
        >
          {createLink.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Link2 className="h-3.5 w-3.5" />
          )}
          {t('relatedRecords.link')}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// RecordPicker — server-side search command palette (mirrors InlineProductSearch).
// ----------------------------------------------------------------------------

function RecordPicker({
  excludeIds,
  value,
  onPick,
}: {
  excludeIds: string[];
  value: SearchRecordResult | null;
  onPick: (record: SearchRecordResult) => void;
}) {
  const { t } = useTranslation('productDev');
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const search = useDebounced(term);
  const searchQuery = useSearchRecords(search, open);
  const exclude = new Set(excludeIds);
  const options = (searchQuery.data ?? []).filter((o) => !exclude.has(o.id));

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setTerm('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 w-full justify-between font-normal', !value && 'text-muted-foreground')}
        >
          <span className="truncate">
            {value ? value.style_name : t('relatedRecords.chooseRecord')}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('relatedRecords.searchRecords')}
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            <CommandEmpty>
              {term.trim().length < 2
                ? t('relatedRecords.typeToSearch')
                : searchQuery.isFetching
                  ? t('relatedRecords.searching')
                  : t('relatedRecords.noResults')}
            </CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt.id}
                value={opt.id}
                onSelect={() => {
                  onPick(opt);
                  setOpen(false);
                }}
                className="gap-2"
              >
                <span className="min-w-0 flex-1 truncate">{opt.style_name}</span>
                {opt.style_code && (
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {opt.style_code}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
